/**
 * Service de tracking des visites
 * Compte les visites sans les afficher dans l'interface
 */

class VisitTracker {
  constructor() {
    this.STORAGE_KEY = 'wd_visit_tracker';
    this.SESSION_KEY = 'wd_session_id';
    this.API_ENDPOINT = process.env.REACT_APP_VISIT_API_ENDPOINT || null;
  }

  /**
   * Génère un identifiant de session unique
   */
  generateSessionId() {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * Récupère ou crée une session ID
   */
  getSessionId() {
    let sessionId = sessionStorage.getItem(this.SESSION_KEY);
    if (!sessionId) {
      sessionId = this.generateSessionId();
      sessionStorage.setItem(this.SESSION_KEY, sessionId);
    }
    return sessionId;
  }

  /**
   * Vérifie si c'est une nouvelle visite (nouvelle session)
   */
  isNewVisit() {
    const sessionId = this.getSessionId();
    const lastVisit = localStorage.getItem(this.STORAGE_KEY);
    
    if (!lastVisit) {
      return true;
    }

    try {
      const visitData = JSON.parse(lastVisit);
      // Si la session ID est différente, c'est une nouvelle visite
      return visitData.sessionId !== sessionId;
    } catch (e) {
      return true;
    }
  }

  /**
   * Enregistre une nouvelle visite (LOCAL UNIQUEMENT).
   * N'émet AUCUN appel réseau : l'envoi vers l'API est la responsabilité
   * exclusive de trackPageView(), appelé à chaque changement de chemin —
   * y compris le premier rendu. Émettre ici aussi ferait compter deux fois
   * la page d'entrée de chaque nouvelle session dans PageViews / ViewsByPath.
   */
  trackVisit() {
    const sessionId = this.getSessionId();
    const now = new Date().toISOString();
    
    // Récupérer le compteur actuel
    let visitCount = 0;
    const existingData = localStorage.getItem(this.STORAGE_KEY);
    if (existingData) {
      try {
        const data = JSON.parse(existingData);
        visitCount = data.totalVisits || 0;
      } catch (e) {
        // Ignorer les erreurs de parsing
      }
    }
    
    const visitData = {
      sessionId,
      timestamp: now,
      url: window.location.href,
      path: window.location.pathname,
      referrer: document.referrer || 'direct',
      userAgent: navigator.userAgent,
      totalVisits: visitCount + 1,
      lastVisit: now,
    };

    // Sauvegarder localement (aucun envoi réseau ici, cf. docstring)
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(visitData));

    return visitData;
  }

  /**
   * Envoie les données de visite à l'API backend
   */
  async sendToAPI(visitData) {
    if (!this.API_ENDPOINT) {
      return;
    }

    try {
      const axios = (await import('axios')).default;
      await axios.post(this.API_ENDPOINT, {
        ...visitData,
        type: 'page_view',
      }, {
        timeout: 5000,
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
      // Ne pas bloquer l'application si l'API est indisponible
      console.warn('Impossible d\'envoyer la visite à l\'API:', error.message);
    }
  }

  /**
   * Enregistre une vue de page (à chaque navigation SPA)
   * Indépendant de la dé-dup de session : émet un POST à CHAQUE changement
   * de chemin afin d'alimenter les métriques PageViews / ViewsByPath.
   * Le compteur local n'est PAS incrémenté ici (réservé aux visites uniques).
   */
  async trackPageView(path, referrer) {
    const sessionId = this.getSessionId();
    const now = new Date().toISOString();

    const resolvedPath = path || window.location.pathname;
    const resolvedReferrer = referrer || document.referrer || 'direct';

    const pageViewData = {
      sessionId,
      timestamp: now,
      url: window.location.href,
      path: resolvedPath,
      referrer: resolvedReferrer,
      userAgent: navigator.userAgent,
    };

    // Envoyer à l'API si un endpoint est configuré (no-op réseau sinon)
    if (this.API_ENDPOINT) {
      this.sendToAPI(pageViewData).catch((error) => {
        console.warn('Erreur lors de l\'envoi de la vue de page à l\'API:', error);
      });
    }

    return pageViewData;
  }

  /**
   * Récupère le nombre total de visites (stocké localement)
   * Note: Pour un compteur global précis, utilisez un backend
   */
  getLocalVisitCount() {
    const visitData = localStorage.getItem(this.STORAGE_KEY);
    if (!visitData) {
      return 0;
    }
    try {
      const data = JSON.parse(visitData);
      return data.totalVisits || 0;
    } catch (e) {
      return 0;
    }
  }

  /**
   * Initialise le tracking d'une visite : met à jour le compteur local de
   * visites uniques par session. Purement local — les métriques distantes
   * proviennent de trackPageView().
   */
  init() {
    if (this.isNewVisit()) {
      this.trackVisit();
    }
  }
}

// Instance singleton
const visitTracker = new VisitTracker();

export default visitTracker;

